import { EChatMessageSource } from '@adaptiva/core/chat';
import { ClipboardService } from '@adaptiva/core/clipboard';
import { MenuItem } from '@adaptiva/core/menus';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LlmConversationNode } from '../classes';
import { ILlmPayload } from '../interfaces';
import { CHAT_MENU_CONTEXT } from '../tokens';
import { BaseLlmNodeMenuController } from './base-llm-node-menu-controller';

@Injectable()
export class LlmChatMenuController extends BaseLlmNodeMenuController {

	public override readonly createForkMenuName: string = 'Fork';

	public override readonly showHistoryMenuName: string = 'History';

	public readonly copyMenuName: string = 'Copy';

	protected readonly chatMenuContext: EChatMessageSource = inject(CHAT_MENU_CONTEXT);

	protected readonly clipboard: ClipboardService = inject(ClipboardService);

	protected readonly snackbars: SnackbarService = inject(SnackbarService);

	public override generate(_node: LlmConversationNode<ILlmPayload>): Observable<MenuItem<LlmConversationNode<ILlmPayload>>[]> {
		const _menus: MenuItem<LlmConversationNode>[] = [
			this.getCopyMenuItem(_node),
			this.getOpenInHistoryMenuItem(_node),
		];

		let _shouldShowFork: boolean = true;

		if (!_node.parent)
			_shouldShowFork = !this.aida.getConversationNode(_node.nodeId);

		if (this.chatMenuContext === EChatMessageSource.RESPONSE && _shouldShowFork)
			_menus.push(this.getCreateForkMenuItem(_node));

		return of(_menus);
	}

	public override getOpenInHistoryMenuItem(_node: LlmConversationNode<ILlmPayload>): MenuItem<LlmConversationNode<ILlmPayload>> {
		const _menu: MenuItem = super.getOpenInHistoryMenuItem(_node);

		_menu.icon.next({ icon: 'faClockRotateLeft', color: 'foreground' });

		return _menu;
	}

	public override getCreateForkMenuItem(_node: LlmConversationNode<ILlmPayload>): MenuItem<LlmConversationNode<ILlmPayload>> {
		const _menu: MenuItem = super.getCreateForkMenuItem(_node);

		_menu.icon.next({ icon: 'faCodeFork', color: 'foreground' });

		return _menu;
	}

	public getCopyMenuItem(_node: LlmConversationNode): MenuItem<LlmConversationNode> {
		const _itemId: string = this.hashItem(this.copyMenuName, _node);
		const _item: MenuItem<LlmConversationNode> = this.getItem(_itemId, _node) || this.createItem({
			id: _itemId,
			name: this.copyMenuName,
			data: _node,
			onSelect: this.copy.bind(this),
			icon: new BehaviorSubject({ icon: 'faCopy', color: 'foreground' })
		});

		return _item;
	}

	protected override hashItem(_key: string, _node: LlmConversationNode): string {
		return `${super.hashItem(_key, _node)}-${this.chatMenuContext}`;
	}

	protected copy(_menu: MenuItem<LlmConversationNode>): void {
		const _node: LlmConversationNode = _menu.data;
		const _message: string = this.chatMenuContext === EChatMessageSource.RESPONSE ? _node.llmResponse : _node.userMessage;

		this.clipboard.copy(_message)
			.then(() => this.snackbars.display('Copied to clipboard'))
			.catch(() => this.snackbars.display('System clipboard not supported'));
	}

}
